import L from 'leaflet'
import { TARKOV_MAP_DEFS } from './tarkovMapsData'

/**
 * tarkov.dev のマップ SVG と、ゲーム内座標 → Leaflet の変換。
 *
 * ゲームの座標は `{ x, y, z }` の3つ組で、**y は高さ**。平面は x（東西）と z（南北）。
 * Leaflet には `[lat, lng] = [z, x]` で渡す（→ `gamePos`）。
 * そのうえでマップごとの回転・拡大・平行移動を CRS に持たせる（→ `crsFor`）。
 *
 * 変換の中身は the-hideout/tarkov-dev (MIT) の `src/pages/map/index.jsx` の写し。
 * 設定値は `src/tarkovMapsData.ts`（自動生成）にある。
 *
 * SVG そのものは CC BY-NC-SA 4.0。**無改変で敷き、ラベルやピンは上に重ねる**。
 * SVG のファイルを書き換えないかぎり SA 条項は発動しない。
 */

/** ゲーム内座標。y は高さ */
export type GamePosition = { x: number; y: number; z: number }

/** 通り名・建物名。`position` はゲーム座標の [x, z] */
export type MapLabel = {
  position: [number, number]
  text: string
  /** 度。地図に対する傾き */
  rotation?: number
  size?: number
  /** 出すフロアの高さ。無ければどの高さでも出す */
  top?: number
  bottom?: number
}

/** フロアが受け持つ範囲。建物ごとに高さが違うマップでは複数に分かれる */
export type MapLayerExtent = {
  /** [下端, 上端] */
  height: [number, number]
  /** [左上, 右下, 覚え書き]。座標はゲームの [x, z] */
  bounds?: [[number, number], [number, number], string][]
}

/** SVG の中のフロア。`svgLayer` は `<g id="Second_Floor">` の id */
export type MapLayer = {
  name: string
  svgLayer: string
  /** 既定で表示するか */
  show: boolean
  /** extents をぜんぶ包んだ高さ */
  height?: [number, number]
  extents?: MapLayerExtent[]
}

export type TarkovMapDef = {
  /** 'customs'。このアプリでマップを指すキー */
  normalizedName: string
  /** '56f40101d2720b2a4d8b45d6'。zone の `map` と突き合わせる id */
  id: string
  displayName: string
  /** public/ からの絶対パス */
  svg: string
  /** 地上フロアの `<g>` id */
  svgLayer: string
  /** [scaleX, marginX, scaleY, marginY] */
  transform: [number, number, number, number]
  /** 度 */
  coordinateRotation: number
  /** ゲーム座標の [[x, z], [x, z]] */
  bounds: [[number, number], [number, number]]
  /** SVG の縁が bounds とずれるマップだけ持つ */
  svgBounds?: [[number, number], [number, number]]
  /** 地上フロアの高さ */
  heightRange?: [number, number]
  minZoom?: number
  maxZoom?: number
  /** 同じ場所を指す別のマップ名（'night-factory' など） */
  altNames?: string[]
  layers?: MapLayer[]
  labels?: MapLabel[]
  /** SVG の作者。表示義務がある */
  author: string
  authorLink: string
}

/** normalizedName で引く。SVG のある10マップだけ */
export const TARKOV_MAPS: Record<string, TarkovMapDef> = TARKOV_MAP_DEFS

export const TARKOV_MAP_LIST: TarkovMapDef[] = Object.values(TARKOV_MAP_DEFS)

/**
 * normalizedName・マップ id・別名のどれでも引ける。
 * zone の `map` は id で来ることも `ground-zero-21` のような別名で来ることもある。
 */
export function findMap(key: string | null | undefined): TarkovMapDef | null {
  if (!key) return null
  const direct = TARKOV_MAPS[key]
  if (direct) return direct
  return (
    TARKOV_MAP_LIST.find((m) => m.id === key || m.altNames?.includes(key)) ?? null
  )
}

/** ゲーム座標 → Leaflet の latLng。y（高さ）は平面には効かない */
export function gamePos(p: { x: number; z: number }): [number, number] {
  return [p.z, p.x]
}

/** ラベルや extents の [x, z] を latLng へ */
export const flatPos = (p: [number, number]): [number, number] => [p[1], p[0]]

/** 原点まわりに回す */
function applyRotation(latLng: L.LatLng, rotation: number): L.LatLng {
  if (!rotation) return latLng
  const rad = (rotation * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const { lng: x, lat: y } = latLng
  return L.latLng(x * sin + y * cos, x * cos - y * sin)
}

const crsCache = new Map<string, L.CRS>()

/**
 * そのマップ用の CRS を作る。`L.map(el, { crs: crsFor(map) })` に渡す。
 *
 * `transform` は [scaleX, marginX, scaleY, marginY] だが、
 * **scaleY だけ符号を反転させる**。落とすと南北が裏返る。
 */
export function crsFor(map: TarkovMapDef): L.CRS {
  const cached = crsCache.get(map.normalizedName)
  if (cached) return cached

  const [scaleX, marginX, scaleY, marginY] = map.transform
  const crs = L.extend({}, L.CRS.Simple, {
    transformation: new L.Transformation(scaleX, marginX, scaleY * -1, marginY),
    projection: L.extend({}, L.Projection.LonLat, {
      project: (latLng: L.LatLng) =>
        L.Projection.LonLat.project(applyRotation(latLng, map.coordinateRotation)),
      unproject: (point: L.Point) =>
        applyRotation(L.Projection.LonLat.unproject(point), map.coordinateRotation * -1),
    }),
  }) as L.CRS

  crsCache.set(map.normalizedName, crs)
  return crs
}

/** [[x, z], [x, z]] を Leaflet の緯度経度の順に入れ替える */
const toLatLngBounds = (b: [[number, number], [number, number]]): L.LatLngBounds =>
  L.latLngBounds([b[0][1], b[0][0]], [b[1][1], b[1][0]])

/** マップの範囲。fitBounds や maxBounds に使う */
export const boundsFor = (map: TarkovMapDef): L.LatLngBounds => toLatLngBounds(map.bounds)

/** SVG を敷くときの範囲。ふつうは bounds と同じで、Reserve だけずれる */
export const svgBoundsFor = (map: TarkovMapDef): L.LatLngBounds =>
  toLatLngBounds(map.svgBounds ?? map.bounds)

/** SVG のトップレベルにある、id 付きの `<g>`。これがフロアの単位になっている */
const layerGroups = (svg: SVGSVGElement): SVGGElement[] =>
  [...svg.children].filter((c): c is SVGGElement => c.nodeName === 'g' && !!c.id)

/**
 * 表示するフロアを切り替える。
 *
 * SVG には全フロアが重ねて入っているので、そのまま敷くと地上と2階が混ざる。
 * 地上（`map.svgLayer`）は常に出し、それ以外は `visibleLayers` に挙がったものだけ出す。
 *
 * DOM 上で `display` を変えているだけで、SVG のファイルには手を入れていない。
 */
export function setVisibleLayers(
  svg: SVGSVGElement,
  map: TarkovMapDef,
  visibleLayers: string[] = [],
): void {
  for (const group of layerGroups(svg)) {
    const isBase =
      group.id === map.svgLayer || group.dataset.keepWithGroup === map.svgLayer
    group.style.display = isBase || visibleLayers.includes(group.id) ? '' : 'none'
  }
}

/**
 * SVG を読んで `L.svgOverlay` に渡せる形にする。
 *
 *   const svg = await loadMapSvg(map)
 *   L.svgOverlay(svg, svgBoundsFor(map)).addTo(leafletMap)
 */
export async function loadMapSvg(
  map: TarkovMapDef,
  visibleLayers?: string[],
): Promise<SVGSVGElement> {
  const res = await fetch(map.svg)
  if (!res.ok) throw new Error(`${map.svg} が読めない — HTTP ${res.status}`)
  const doc = new DOMParser().parseFromString(await res.text(), 'image/svg+xml')
  if (doc.documentElement.nodeName !== 'svg') throw new Error(`${map.svg} が SVG として読めない`)
  // 別ドキュメントのノードのままだと扱いが処理系任せになるので、こちらへ取り込む
  const svg = document.importNode(doc.documentElement, true) as unknown as SVGSVGElement

  // svgOverlay は viewBox しか見ない。width/height が付いていると縦横比が狂う
  svg.removeAttribute('width')
  svg.removeAttribute('height')
  setVisibleLayers(svg, map, visibleLayers ?? defaultVisibleLayers(map))
  return svg
}

/** maps.json が「既定で出す」と言っているフロア */
export const defaultVisibleLayers = (map: TarkovMapDef): string[] =>
  (map.layers ?? []).filter((l) => l.show).map((l) => l.svgLayer)

/**
 * その高さがフロアの受け持ちに入るか。tarkov.dev の `markerIsOnLayer` と同じ判定。
 * `'full'`（すっぽり入る）/ `'partial'`（またいでいる）/ `false`。
 */
export function layerContains(
  layer: MapLayer,
  position: { x: number; z: number },
  top: number,
  bottom: number,
): 'full' | 'partial' | false {
  const extents = layer.extents ?? (layer.height ? [{ height: layer.height }] : [])
  for (const extent of extents) {
    if (top < extent.height[0] || bottom >= extent.height[1]) continue
    const contained = bottom >= extent.height[0] && top <= extent.height[1] ? 'full' : 'partial'
    if (!extent.bounds) return contained
    for (const b of extent.bounds) {
      if (toLatLngBounds([b[0], b[1]]).contains(gamePos(position))) return contained
    }
  }
  return false
}

/**
 * ゲーム座標を、マップ全体に敷いた SVG オーバーレイの viewBox 座標へ移す。
 *
 * `L.svgOverlay` は bounds の北西・南東を投影した画面上の矩形に viewBox を貼るだけなので、
 * **同じ投影を通してから矩形内の相対位置に直せば、回転があっても SVG と重なる**。
 * 落書きのように「1枚の SVG に描いてズームで一緒に太らせたいもの」に使う。
 *
 * 縦を 1000 に固定した座標系にしているのは、線幅と消しゴムの大きさを
 * マップに依らず同じ見た目にするため（マップごとに縮尺が違うので生の値は使えない）。
 */
export const FRAME_HEIGHT = 1000

export type MapFrame = {
  /** viewBox の幅。高さは FRAME_HEIGHT 固定 */
  width: number
  height: number
  /** ゲーム座標 → viewBox 座標 */
  toFrame(p: { x: number; z: number }): [number, number]
  /** viewBox 座標 → ゲーム座標の [x, z] */
  toGame(fx: number, fy: number): [number, number]
}

const frameCache = new Map<string, MapFrame>()

export function frameFor(map: TarkovMapDef): MapFrame {
  const cached = frameCache.get(map.normalizedName)
  if (cached) return cached

  const crs = crsFor(map)
  // 倍率は最後に割り算で消えるので、どのズームで測っても結果は同じ
  const project = (lat: number, lng: number) => crs.latLngToPoint(L.latLng(lat, lng), 0)
  const unproject = (x: number, y: number) => crs.pointToLatLng(L.point(x, y), 0)

  const b = boundsFor(map)
  const a = project(b.getNorth(), b.getWest())
  const c = project(b.getSouth(), b.getEast())
  const minX = Math.min(a.x, c.x)
  const minY = Math.min(a.y, c.y)
  const spanX = Math.abs(c.x - a.x)
  const spanY = Math.abs(c.y - a.y)
  // 投影は回転＋等倍の相似変換（transform の scaleX と scaleY は全マップで同じ）なので、
  // 縦横比を保ったまま高さ 1000 に合わせられる
  const k = FRAME_HEIGHT / spanY

  const frame: MapFrame = {
    width: spanX * k,
    height: FRAME_HEIGHT,
    toFrame(p) {
      const q = project(p.z, p.x)
      return [(q.x - minX) * k, (q.y - minY) * k]
    },
    toGame(fx, fy) {
      const ll = unproject(fx / k + minX, fy / k + minY)
      return [ll.lng, ll.lat]
    },
  }

  frameCache.set(map.normalizedName, frame)
  return frame
}

/**
 * その位置を、いま見えているフロアで出してよいか。
 * tarkov.dev の `checkMarkerForActiveLayers` と同じ考え方で、
 * **「見えていないフロアにすっぽり入っているもの」だけを隠す**。
 * 高さの情報が無いもの（top/bottom なし）は常に出す。
 */
export function visibleOnLayers(
  map: TarkovMapDef,
  visibleLayers: string[],
  position: { x: number; z: number },
  top: number | null | undefined,
  bottom: number | null | undefined,
): boolean {
  if (top == null || bottom == null) return true
  for (const layer of map.layers ?? []) {
    if (visibleLayers.includes(layer.svgLayer)) continue
    if (layerContains(layer, position, top, bottom) === 'full') return false
  }
  return true
}
